<template>
  <div class="installation-data">
    <span>
      <b>Status:</b> {{ client.tasks.installation.status }}
      <span v-if="statusAge" class="has-text-grey" :title="client.tasks.installation.status_at">({{ statusAge }})</span>
    </span>
    <span v-if="installers.length > 0"> <b>Installateur:</b> {{ installers.join() }} </span>
    <span class="pb-2" v-if="client.tasks.installation.appointment_date">
      <b>Appointment date:</b> {{ client.tasks.installation.appointment_date }}
    </span>
    <installation-documents :address="client.fullAddress" :project="client.project || null" />
    <p v-if="!productGroups.length" class="has-text-grey pt-2">Geen geïnstalleerde producten</p>
    <div v-for="group in productGroups" :key="group.type" class="pt-2">
      <p class="product-type">{{ group.label }}</p>
      <div v-for="(product, i) in group.products" :key="`${group.type}-${i}`">
        <p class="product">{{ product.amount }} x {{ product.description || group.label }}</p>
        <p v-if="product.serialNumbers.length" class="serials">{{ product.serialNumbers.join(', ') }}</p>
      </div>
    </div>
    <ticket-photos
      :address="client.fullAddress"
      task-type="installation"
      :project="client.project || null"
      label="installatie"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import TicketPhotos from './TicketPhotos.vue'
import InstallationDocuments from './InstallationDocuments.vue'
import { currentTicket } from './currentTicket'
import { formatRelativeNl } from '@/util/DateTime'
import { groupProductsByType } from '@/util/productTypes'

@Component({ components: { TicketPhotos, InstallationDocuments } })
export default class InstallationData extends Vue {
  @Prop({}) client!: any

  get statusAge() {
    return formatRelativeNl(this.client.tasks.installation.status_at)
  }

  private formatProducts(products: any) {
    const newProducts: Array<any> = []

    Object.entries(products).forEach(([type, value]: Array<any>) => {
      if (!value) return

      Object.entries(value).forEach((prod: Array<any>) => {
        const newProduct = {
          type,
          description: '',
          amount: 0,
          power: '',
          serialNumbers: [],
        }

        const { amount } = prod[1]
        const serialNumbers = prod[1].serial_numbers
        const productType = prod[1].description
        const power = prod[1].W || prod[1].wp

        if (productType) {
          newProduct.description = productType
        }
        if (amount) {
          newProduct.amount = amount
        }
        if (power) {
          newProduct.power = power
        }
        if (serialNumbers) {
          newProduct.serialNumbers = serialNumbers
        }

        newProducts.push(newProduct)
      })
    })

    return newProducts
  }

  get products() {
    let products: Array<any> = []

    if (!this.client.tasks.installation.tickets) return products

    // only the current ticket: a cancelled one can carry products that were never installed
    const ticket: any = currentTicket(this.client.tasks.installation.tickets)
    if (ticket?.output?.products) products = this.formatProducts(ticket.output.products)

    return products
  }

  get productGroups() {
    // a product registered with serial numbers but without an amount still counts (one per serial)
    return groupProductsByType(
      this.products.map((p: any) => ({ ...p, amount: p.amount || p.serialNumbers.length }))
    )
  }

  get installers() {
    if (!this.client.tasks.installation.tickets) return []

    // the installer of the current ticket, not of cancelled earlier attempts
    const ticket: any = currentTicket(this.client.tasks.installation.tickets)
    return ticket?.partner ? [ticket.partner] : []
  }
}
</script>

<style lang="scss" scoped>
.installation-data {
  display: flex;
  flex-direction: column;

  .product-type {
    font-size: 0.75em;
    font-weight: bold;
    text-transform: uppercase;
    color: #7a7a7a;
    letter-spacing: 0.03em;
  }

  .product {
    font-size: 1.2em;
  }

  .serials {
    font-size: 0.85em;
    color: #7a7a7a;
    word-break: break-all;
  }

  & > * {
    b {
      padding-right: 0.3rem;
    }
  }
}
</style>
